import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ContextSession } from "../../services/ContextSession";
import {
  apiRequest,
  buildApiUrl,
  getAccessToken,
  mapDesignacionSearchToUi,
} from "./enrolamientoApi";
import {
  formatIdentificacionVisual,
  stripIdentificacionVisual,
} from "./identificacionUtils";

const UI_PRIMARY = "#19498c";
const ENTIDADES_ADJUNTO_CONSULTA = ["DESIGNACION", "BENEFICIARIO"];

const t = (v) => (v === undefined || v === null ? "" : String(v).trim());
const n = (v, f = 0) => (Number.isFinite(Number(v)) ? Number(v) : f);
const sumPct = (rows = []) =>
  rows.reduce((acc, x) => acc + n(x?.porcentaje, 0), 0);
const stateColor = (s) =>
  s === "ACTIVO" ? "success" : s === "EN PROCESO" ? "warning" : "default";
const formatFechaHistorico = (value) => {
  const raw = t(value);
  if (!raw || raw === "0000-00-00" || raw === "0000-00-00 00:00:00") return "";

  const dotNetDate = raw.match(/\/Date\((\d+)\)\//);
  if (dotNetDate) {
    const fromTicks = new Date(Number(dotNetDate[1]));
    return Number.isNaN(fromTicks.getTime())
      ? ""
      : fromTicks.toLocaleDateString("es-HN");
  }

  const ddmmyyyy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (ddmmyyyy) return `${ddmmyyyy[1]}/${ddmmyyyy[2]}/${ddmmyyyy[3]}`;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("es-HN");
};
const sanitizeAdjuntoPath = (path) => {
  const raw = t(path);
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw.replace("/api/api/", "/api/");
  return raw.replace(/^\/api\/api\//i, "/api/");
};
const resolveAdjuntoUrl = (path) => {
  const sanitized = sanitizeAdjuntoPath(path);
  if (!sanitized) return "";
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const withoutApiPrefix = sanitized.startsWith("/api/")
    ? sanitized.slice(4)
    : sanitized;
  return buildApiUrl(withoutApiPrefix);
};
const mapAdjuntos = (rows = []) =>
  (rows || []).map((item, index) => {
    const nombre = item?.Nombre || item?.ArchivoNombre || "";
    const tipo =
      item?.Tipo || (nombre.includes(".") ? `.${nombre.split(".").pop()}` : "");
    const downloadPath =
      item?.download_url ||
      (item?.AdjuntoId
        ? `/Enrolamiento/adjuntos/${item.AdjuntoId}/descargar/`
        : item?.ruta || item?.Ruta || "");
    return {
      id: item?.AdjuntoId || `adj-${index}`,
      nombre,
      descripcion: item?.Descripcion || item?.Observaciones || nombre,
      entidad: t(item?.Entidad || item?.entidad).toUpperCase(),
      registro: t(item?.Registro || item?.registro),
      tipo,
      fechaCrea: item?.FechaCrea || item?.fechaCrea || "",
      downloadUrl: resolveAdjuntoUrl(downloadPath),
    };
  });

export default function ConsultarDesignacion() {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";

  const [dni, setDni] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [result, setResult] = useState(null);
  const [adjuntos, setAdjuntos] = useState([]);
  const [tab, setTab] = useState(0);
  const [registroAdjuntoFiltro, setRegistroAdjuntoFiltro] = useState("");
  const [historicoBeneficiariosOpen, setHistoricoBeneficiariosOpen] =
    useState(false);
  const [historicoDesignacion, setHistoricoDesignacion] = useState(null);
  const [previewAdjuntoOpen, setPreviewAdjuntoOpen] = useState(false);
  const [previewAdjuntoUrl, setPreviewAdjuntoUrl] = useState("");
  const [previewAdjuntoMime, setPreviewAdjuntoMime] = useState("");
  const [previewAdjuntoError, setPreviewAdjuntoError] = useState("");
  const [previewAdjuntoLoading, setPreviewAdjuntoLoading] = useState(false);

  const designaciones = result?.designaciones || [];
  const hasDesignaciones = designaciones.length > 0;
  const desActiva = designaciones.find((x) => x.estado === "ACTIVO") || null;
  const desConsultaPrincipal =
    desActiva ||
    designaciones.find(
      (x) => String(x?.estado || "").trim().toUpperCase() !== "EN PROCESO",
    ) ||
    designaciones[0] ||
    null;
  const designacionesHistorico = designaciones.filter(
    (x) => String(x?.estado || "").trim().toUpperCase() !== "EN PROCESO",
  );
  const beneficiariosActuales = desConsultaPrincipal?.beneficiarios || [];
  const totalPct = sumPct(beneficiariosActuales);

  const adjuntosConsulta = useMemo(
    () =>
      (adjuntos || []).filter((item) =>
        ENTIDADES_ADJUNTO_CONSULTA.includes(
          String(item?.entidad || "").trim().toUpperCase(),
        ),
      ),
    [adjuntos],
  );
  const adjuntosConsultaFiltrados = useMemo(() => {
    const filtro = t(registroAdjuntoFiltro);
    if (!filtro) return adjuntosConsulta;
    return adjuntosConsulta.filter((item) => t(item?.registro) === filtro);
  }, [adjuntosConsulta, registroAdjuntoFiltro]);

  useEffect(
    () => () => {
      if (previewAdjuntoUrl) URL.revokeObjectURL(previewAdjuntoUrl);
    },
    [previewAdjuntoUrl],
  );

  const refreshAdjuntos = async (personaId) => {
    if (!personaId) return;
    try {
      const data = await apiRequest(
        `/Enrolamiento/personas/${encodeURIComponent(personaId)}/adjuntos/?entidad=afiliacion`,
        { authToken },
      );
      setAdjuntos(mapAdjuntos(data?.adjuntos || []));
    } catch (_e) {
      setAdjuntos([]);
    }
  };

  const loadByDni = async (dniValue) => {
    setLoading(true);
    try {
      const data = await apiRequest(
        `/Enrolamiento/designaciones/buscar/?dni=${encodeURIComponent(dniValue)}&tipo=L`,
        { authToken },
      );
      const ui = mapDesignacionSearchToUi(data);
      setResult(ui);
      setRegistroAdjuntoFiltro("");
      setTab(0);
      await refreshAdjuntos(ui.personaId);
    } finally {
      setLoading(false);
    }
  };

  const buscar = async () => {
    if (!t(dni)) return setError("Ingrese el DNI.");
    try {
      setError("");
      setOk("");
      await loadByDni(dni);
      setOk("Consulta realizada.");
    } catch (e) {
      setResult(null);
      setAdjuntos([]);
      setError(e?.message || "No se pudo consultar.");
      setOk("");
    }
  };

  const openHistoricoBeneficiarios = (designacion) => {
    setHistoricoDesignacion(designacion || null);
    setHistoricoBeneficiariosOpen(true);
  };

  const closePreviewAdjunto = () => {
    setPreviewAdjuntoOpen(false);
    setPreviewAdjuntoLoading(false);
    setPreviewAdjuntoError("");
    if (previewAdjuntoUrl) URL.revokeObjectURL(previewAdjuntoUrl);
    setPreviewAdjuntoUrl("");
    setPreviewAdjuntoMime("");
  };

  const openAdjunto = async (item) => {
    const url = t(item?.downloadUrl);
    if (!url) {
      setError("El adjunto seleccionado no tiene una ruta válida.");
      return;
    }
    setPreviewAdjuntoOpen(true);
    setPreviewAdjuntoLoading(true);
    setPreviewAdjuntoError("");
    if (previewAdjuntoUrl) {
      URL.revokeObjectURL(previewAdjuntoUrl);
      setPreviewAdjuntoUrl("");
      setPreviewAdjuntoMime("");
    }
    try {
      const token = getAccessToken(authToken);
      const headers = token ? { Authorization: token } : {};
      const response = await fetch(url, { method: "GET", headers });
      if (!response.ok) {
        throw new Error(`No se pudo abrir el adjunto (HTTP ${response.status}).`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewAdjuntoUrl(blobUrl);
      setPreviewAdjuntoMime(blob?.type || "application/octet-stream");
    } catch (e) {
      setPreviewAdjuntoError(e?.message || "No se pudo abrir el adjunto.");
    } finally {
      setPreviewAdjuntoLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.2 },
        background:
          "linear-gradient(180deg, rgba(25,73,140,0.08) 0%, rgba(173,132,241,0.08) 100%)",
        minHeight: "calc(100vh - 90px)",
      }}
    >
      <Paper
        sx={{ p: 2, mb: 1.3, border: "1px solid #dbe6f6", borderRadius: 3 }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1 }}>
          Consultar Beneficiarios Legales
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto auto" },
            gap: 1,
            alignItems: "end",
          }}
        >
          <TextField
            size="small"
            label="DNI"
            value={formatIdentificacionVisual(dni)}
            onChange={(e) => setDni(stripIdentificacionVisual(e.target.value))}
            inputProps={{ maxLength: 15 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={buscar}
            disabled={loading}
            sx={{ bgcolor: UI_PRIMARY, color: "#fff" }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setDni("");
              setResult(null);
              setAdjuntos([]);
              setRegistroAdjuntoFiltro("");
              setTab(0);
              setError("");
              setOk("");
            }}
            sx={{ borderColor: UI_PRIMARY, color: UI_PRIMARY }}
          >
            Limpiar
          </Button>
        </Box>
        {error && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {ok && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {ok}
          </Alert>
        )}
      </Paper>

      {result && (
        <>
          <Paper
            sx={{ p: 2, mb: 1.3, border: "1px solid #dbe6f6", borderRadius: 3 }}
          >
            <Typography sx={{ fontWeight: 700, color: UI_PRIMARY }}>
              {t(
                [
                  result.primerNombre,
                  result.segundoNombre,
                  result.primerApellido,
                  result.segundoApellido,
                ]
                  .join(" ")
                  .replace(/\s+/g, " "),
              )}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#555" }}>
              DNI: {formatIdentificacionVisual(result.dni)}
            </Typography>
            {hasDesignaciones && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#555",
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                Estado actual:{" "}
                <Chip
                  size="small"
                  color={stateColor(desConsultaPrincipal?.estado)}
                  label={desConsultaPrincipal?.estado || "SIN ESTADO"}
                />
              </Typography>
            )}
          </Paper>

          {hasDesignaciones ? (
            <Paper
              sx={{ p: 2, mb: 1.3, border: "1px solid #dbe6f6", borderRadius: 3 }}
            >
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ borderBottom: "1px solid #e5e5e5", mb: 1 }}
              >
                <Tab label="Designacion Actual" />
                <Tab label="Historico" />
                <Tab label="Adjuntos" />
              </Tabs>

              {tab === 0 && (
                <>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>
                    Designacion N°{" "}
                    {t(desConsultaPrincipal?.numero ?? desConsultaPrincipal?.Numero) ||
                      "-"}{" "}
                    ({desConsultaPrincipal?.estado || "SIN ESTADO"})
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          No. Identificacion
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Primer Nombre</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Segundo Nombre</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Primer Apellido</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Segundo Apellido</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Parentesco</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Porcentaje</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!beneficiariosActuales.length ? (
                        <TableRow>
                          <TableCell colSpan={7}>Sin beneficiarios.</TableCell>
                        </TableRow>
                      ) : (
                        beneficiariosActuales.map((b, idx) => (
                          <TableRow key={`actual-ben-${idx}`}>
                            <TableCell>{formatIdentificacionVisual(t(b?.dni))}</TableCell>
                            <TableCell>{t(b?.primerNombre)}</TableCell>
                            <TableCell>{t(b?.segundoNombre)}</TableCell>
                            <TableCell>{t(b?.primerApellido)}</TableCell>
                            <TableCell>{t(b?.segundoApellido)}</TableCell>
                            <TableCell>{t(b?.parentesco)}</TableCell>
                            <TableCell>{n(b?.porcentaje, 0)}%</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <Typography sx={{ mt: 1, fontWeight: 700 }}>
                    Total porcentaje legal: {totalPct}%
                  </Typography>
                </>
              )}

              {tab === 1 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>N°</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fecha Inicio</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fecha Fin</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!designacionesHistorico.length ? (
                      <TableRow>
                        <TableCell colSpan={4}>Sin historico.</TableCell>
                      </TableRow>
                    ) : (
                      designacionesHistorico.map((d) => (
                        <TableRow
                          key={d.idDesignacion}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            setRegistroAdjuntoFiltro(
                              String(d?.idDesignacion ?? "").trim(),
                            );
                            openHistoricoBeneficiarios(d);
                          }}
                        >
                          <TableCell>{t(d?.numero ?? d?.Numero) || "-"}</TableCell>
                          <TableCell>{formatFechaHistorico(d.fechaInicio)}</TableCell>
                          <TableCell>{formatFechaHistorico(d.fechaFinalizo)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={stateColor(d.estado)}
                              label={d.estado || "SIN ESTADO"}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}

              {tab === 2 && (
                <>
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 13.5, color: "#5f5f5f" }}>
                      {registroAdjuntoFiltro
                        ? `Mostrando adjuntos de la designación ${registroAdjuntoFiltro}.`
                        : "Mostrando adjuntos de DESIGNACION y BENEFICIARIO."}
                    </Typography>
                    {registroAdjuntoFiltro && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setRegistroAdjuntoFiltro("")}
                        sx={{ borderColor: UI_PRIMARY, color: UI_PRIMARY }}
                      >
                        Mostrar todos
                      </Button>
                    )}
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Fecha creación</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Ver adjunto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!adjuntosConsultaFiltrados.length ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            No hay adjuntos para consulta.
                          </TableCell>
                        </TableRow>
                      ) : (
                        adjuntosConsultaFiltrados.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{t(item?.descripcion || item?.nombre) || "-"}</TableCell>
                            <TableCell>{formatFechaHistorico(item?.fechaCrea)}</TableCell>
                            <TableCell>{t(item?.tipo) || "-"}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => openAdjunto(item)}
                                disabled={!t(item?.downloadUrl)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </>
              )}
            </Paper>
          ) : (
            <Alert severity="info" sx={{ mb: 1.3 }}>
              No se encontró Designación en Enrolamiento.
            </Alert>
          )}
        </>
      )}

      <Dialog
        open={previewAdjuntoOpen}
        onClose={closePreviewAdjunto}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle sx={{ pb: 1 }}>Vista previa de adjunto</DialogTitle>
        <DialogContent dividers>
          {previewAdjuntoLoading ? (
            <Typography sx={{ color: "#5f5f5f" }}>Cargando adjunto...</Typography>
          ) : previewAdjuntoError ? (
            <Alert severity="error">{previewAdjuntoError}</Alert>
          ) : previewAdjuntoUrl ? (
            <>
              <iframe
                title="adjunto-preview"
                src={previewAdjuntoUrl}
                style={{ width: "100%", height: "70vh", border: "none" }}
              />
              <Typography sx={{ mt: 1, fontSize: 12.5, color: "#5f5f5f" }}>
                Tipo detectado: {previewAdjuntoMime || "no identificado"}
              </Typography>
            </>
          ) : (
            <Alert severity="info">No hay archivo para mostrar.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={closePreviewAdjunto}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historicoBeneficiariosOpen}
        onClose={() => setHistoricoBeneficiariosOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Beneficiarios de designación N°{" "}
          {t(historicoDesignacion?.numero ?? historicoDesignacion?.Numero) || "-"}
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>No. Identificación</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Primer Nombre</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Segundo Nombre</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Primer Apellido</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Segundo Apellido</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Parentesco</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Porcentaje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!historicoDesignacion?.beneficiarios?.length ? (
                <TableRow>
                  <TableCell colSpan={7}>Sin beneficiarios.</TableCell>
                </TableRow>
              ) : (
                historicoDesignacion.beneficiarios.map((item, idx) => (
                  <TableRow key={`${historicoDesignacion?.idDesignacion}-${idx}`}>
                    <TableCell>{formatIdentificacionVisual(t(item?.dni))}</TableCell>
                    <TableCell>{t(item?.primerNombre)}</TableCell>
                    <TableCell>{t(item?.segundoNombre)}</TableCell>
                    <TableCell>{t(item?.primerApellido)}</TableCell>
                    <TableCell>{t(item?.segundoApellido)}</TableCell>
                    <TableCell>{t(item?.parentesco)}</TableCell>
                    <TableCell>{n(item?.porcentaje, 0)}%</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setHistoricoBeneficiariosOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
